// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimePickerMulti.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import typeof { DOMElement } from 'react';
import DateTimePickerContainer from './Izenda.DateTimePickerContainer';
import { observable } from 'mobx';
import { PickerDimension } from './Izenda.DateTimeConstants';
import DateTimePickerModel from 'logic/models/Izenda.DateTimePickerModel';
import Moment from 'moment';
import connect from 'share/enhancers/Izenda.Connect';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import actions from './Izenda.DateTimePickerActions';
import { merge, isEmpty, flow, split, map, join, compact } from 'lodash/fp';
import { isDateTimeValid, parseDateTime } from 'share/utils/Izenda.DateTimeUtils';
import ReactDOM from 'react-dom';

const MULTI_VALUE_DELIMITER = ';#';

@connect(actions)
export default class DateTimePickerMulti extends Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    visible: PropTypes.bool.isRequired,
    hidePicker: PropTypes.func.isRequired,
    containerProps: PropTypes.object.isRequired,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    displayFormat: PropTypes.string,
    storeFormat: PropTypes.string,
    renderInContainer: PropTypes.bool,
    disabled: PropTypes.bool,
    name: PropTypes.string,
    placeholder: PropTypes.string
  };

  // Model
  @observable model: DateTimePickerModel = null;

  // Contains array of all the values
  @observable values: Array<string> = null;

  /**
   * Constructor
   * @param {Object} props
   */
  constructor(props: Object) {
    super(props);
    this.model = new DateTimePickerModel();
    this.values = [];
    this.setFormats(props);
    this.setData(props);
  }

  /**
   * Component will receive props
   * @param {Object} nextProps 
   */
  componentWillReceiveProps(nextProps: Object) {
    const {
      displayFormat: prevDisplayFormat,
      storeFormat: prevStoreFormat,
      visible: prevVisible
    } = this.props;
    const { displayFormat, storeFormat, visible } = nextProps;
    (prevDisplayFormat !== displayFormat || prevStoreFormat !== storeFormat) && this.setFormats(nextProps);
    this.setData(nextProps);
    prevVisible && !visible && this.clearInput(nextProps);
  }

  /**
   * Set formats for model
   */
  setFormats(props: Object) {
    const { displayFormat, storeFormat } = props;
    this.model.setFormats(displayFormat, storeFormat);
  }

  /**
   * Set data for models
   */
  setData(props: Object) {
    const { value, displayFormat, storeFormat } = props;
    if (isEmpty(value)) {
      this.values = [];
      return;
    }
    this.values = flow(
      split(MULTI_VALUE_DELIMITER),
      map((item: string) => {
        const dateTime = parseDateTime(item, [displayFormat, storeFormat]);
        return isDateTimeValid(dateTime) ? dateTime.format(displayFormat) : '';
      }),
      compact
    )(value);
  }

  /**
   * Clear the input in select
   */
  clearInput() {
    this.selectInput && this.selectInput.changeInputValue('');
  }

  /**
   * Set anchor for float affixed
   */
  setAnchor = (node: DOMElement) => {
    this.anchor = ReactDOM.findDOMNode(node);
    this.selectInput = node;
  };

  /**
   * On add new value to select
   */
  onAddNew = (newData: Object) => {
    const { displayFormat } = this.props;
    const newDateTime = Moment(newData.value, displayFormat, true);
    return isDateTimeValid(newDateTime);
  };

  /**
   * On select a date in picker
   */
  onSelect = (dateTime: Moment) => {
    const { displayFormat } = this.props;
    this.selectInput.changeInputValue(dateTime.format(displayFormat));
    this.selectInput.focus();
  };

  /**
   * Sync current date time into the select input
   */
  syncDateTimeToInput = () => {
    this.selectInput.changeInputValue(this.model.inputValue);
  };

  /**
   * On change value
   */
  onChange = (value: string) => {
    const { displayFormat, storeFormat, onChange } = this.props;
    const changedValue = isEmpty(value)
      ? value
      : flow(
          split(MULTI_VALUE_DELIMITER),
          map(item => {
            const datetime = Moment(item, displayFormat, true);
            return datetime.format(storeFormat);
          }),
          join(MULTI_VALUE_DELIMITER)
        )(value);

    onChange && onChange(changedValue);
  };

  /**
   * Render control
   */
  renderControl() {
    const { placeholder, onKeyDownInput, onChangeInput, onFocusInput } = this.props;

    // Multi value
    const options = map(item => ({ label: item, value: item }), this.values);
    const selectValue = options.map(item => item.value).join(MULTI_VALUE_DELIMITER) || '';

    return (
      <Select
        ref={this.setAnchor}
        simpleValue
        placeholder={placeholder}
        className='izenda-DateTimePicker-select u_fullWidth'
        controlClass='fitContent'
        delimiter={MULTI_VALUE_DELIMITER}
        textboxMode
        multi
        hiddenMenu
        options={options}
        scalable={false}
        inputMaxHeight={90}
        value={selectValue}
        onAddNew={this.onAddNew}
        onChange={this.onChange}
        onBlurResetsInput={false}
        onInputChange={onChangeInput(this.model, this.syncDateTimeToInput)}
        onInputKeyDown={onKeyDownInput(this.model, this.syncDateTimeToInput)}
        onFocus={onFocusInput(this.model)}
      />
    );
  }

  /**
   * Render
   */
  render() {
    const { calendar, time } = this.props;
    const calendarProps = {
      onSelect: this.onSelect,
      value: this.model.dateTime
    };

    const {
      containerProps: { visible }
    } = this.props;

    // Do not show picker when multi select time
    const containerProps = merge(this.props.containerProps, { visible: visible && (calendar || !time) });

    return (
      <DateTimePickerContainer
        calendarProps={calendarProps}
        anchor={this.anchor}
        pickerAsPopover
        pickerWidth={PickerDimension.SINGLE_WIDTH}
        {...containerProps}
      >
        {this.renderControl()}
      </DateTimePickerContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DateTime/Izenda.DateTimePickerMulti.js